/**
 * UnifiedStepEditor — Main component for the Unified Script Editor.
 *
 * Props:
 *  - scriptId: ID of the script to load/edit (triggers API fetch)
 *  - steps / onStepsChange: controlled mode (optional, bypasses API)
 *  - executionMode: 'frontend' | 'backend' | 'watch' (affects transport actions)
 *  - onRunAll / onRunStep / onStep / onPause / onStop / onRecord: execution callbacks
 *
 * Layout: Tabs (Steps | Variables | Settings | History | Output)
 *   Steps tab = TransportBar + credential picker + StepList + SaveBar
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
// NOTE: this component is reused inside the Chrome extension sidebar
// (panel-react), which bundles with esbuild and is NOT wrapped in a React
// Router. Calling `useNavigate()` unconditionally there throws "useNavigate()
// may be used only in the context of a <Router> component" at mount time and
// bricks the whole panel. See issue #69. We use `useInRouterContext()` to
// guard the hook call: in the dashboard we get real navigation, inside the
// extension we fall back to opening the dashboard route in a new tab via
// chrome.tabs.create().
import { useNavigate, useInRouterContext } from 'react-router-dom';
import {
  Box, Tabs, Tab, TextField, Autocomplete, createFilterOptions,
  Typography, Paper, Table, TableHead, TableRow, TableCell,
  TableBody, Alert, CircularProgress, Collapse, IconButton, Chip, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Radio, RadioGroup, FormControlLabel, FormControl, FormLabel,
} from '@mui/material';
import {
  CheckCircle as PassIcon, Cancel as FailIcon,
  KeyboardArrowRight as ChevronIcon,
  RadioButtonUnchecked as SkippedIcon,
  OpenInNew as ExternalIcon,
} from '@mui/icons-material';

import useStepEditor from './useStepEditor';
import TransportBar from './TransportBar';
import LaunchBar from './LaunchBar';
import StepList from './StepList';
import VariablesPanel from './VariablesPanel';
import SettingsPanel from './SettingsPanel';
import SaveBar from './SaveBar';
import extensionScriptsService from '../../services/api/extensionScriptsService';
import credentialVaultService from '../../services/api/credentialVaultService';
import { ExecutorModeChip } from '../ExecutorModeControls';
import { createStepFromTemplate } from './stepTypes';

/** Format credential for display: "clientId - username" */
const formatCredLabel = (cred) => {
  if (!cred) return '';
  // name is like "Khan Academy - ashishlal", extract username part after " - "
  const parts = (cred.name || '').split(' - ');
  const username = parts.length > 1 ? parts.slice(1).join(' - ') : cred.name || cred.id;
  const site = cred.clientId || '';
  return site ? `${site} - ${username}` : username;
};

// Cap autocomplete results so 3000+ credentials don't choke MUI
const credFilterOptions = createFilterOptions({ limit: 50 });

const TabPanel = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ p: 0 }}>
    {value === index && children}
  </Box>
);

const UnifiedStepEditor = ({
  scriptId,
  steps: controlledSteps,
  onStepsChange,
  executionMode: externalExecMode,
  // Extension sidebar transport callbacks (only used when executionMode === 'extension')
  onRunAll,
  onRunStep,
  onStepForward,
  onPause,
  onStop,
  onRecord,
  // Web editor launch callbacks (used when executionMode !== 'extension')
  onRunInExtension,
  onWatchMode,
  onRunHeadless,
  // #68: when running in the extension, clicking the picker button on a
  // step's Selector field triggers this callback. It receives a one-shot
  // `onPicked({ selector, attributes, ranked, upgraded })` callback. Only
  // the extension sidebar wires it — the web editor passes it through as
  // undefined and the button is hidden.
  onPickSelector,
  // Credential sync: fires when user picks a credential in this editor
  onCredentialChange,
  // External credential ID (synced from extension sidebar or web UI)
  externalCredentialId,
  pendingSteps,
  onPendingStepsConsumed,
  executionProgress,
  executionResult,
}) => {
  const [rightTab, setRightTab] = useState(0);
  const [variables, setVariables] = useState({});
  const [selectedCredentialId, setSelectedCredentialId] = useState('');
  const [executions, setExecutions] = useState([]);
  const [executionsLoading, setExecutionsLoading] = useState(false);
  const [expandedExecutions, setExpandedExecutions] = useState({}); // {execId: true}
  const [recordModeModal, setRecordModeModal] = useState({ open: false, saving: false, error: null });
  // Allow the modal's selected value to ride in local state so we can stage before save
  const [recordModeDraft, setRecordModeDraft] = useState(null);
  // Safe navigate: uses router in the dashboard, falls back to opening the
  // route in a new dashboard tab when running inside the extension sidebar.
  const inRouter = useInRouterContext();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const routerNavigate = inRouter ? useNavigate() : null;
  const navigate = useCallback(
    (to) => {
      if (routerNavigate) {
        routerNavigate(to);
        return;
      }
      // Extension fallback — open the dashboard route in a new tab.
      const base = 'https://automatrix.nucleos.com';
      const url = typeof to === 'string' ? `${base}${to}` : null;
      if (url && typeof window !== 'undefined' && window.chrome?.tabs?.create) {
        try { window.chrome.tabs.create({ url }); return; } catch { /* fall through */ }
      }
      if (url && typeof window !== 'undefined') {
        try { window.open(url, '_blank', 'noopener'); } catch { /* noop */ }
      }
    },
    [routerNavigate]
  );
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [loadingCreds, setLoadingCreds] = useState(false);

  const editor = useStepEditor(scriptId);

  // Determine which steps to use: controlled or from hook
  const isControlled = controlledSteps !== undefined;
  const steps = isControlled ? controlledSteps : editor.steps;
  const script = editor.script;

  // Sync external credential selection (from extension sidebar or web UI)
  useEffect(() => {
    if (externalCredentialId && externalCredentialId !== selectedCredentialId) {
      setSelectedCredentialId(externalCredentialId);
    }
  }, [externalCredentialId]);

  // Load credentials when script's appKey is available
  useEffect(() => {
    const appKey = script?.appKey;
    if (!appKey) return;
    setLoadingCreds(true);
    credentialVaultService
      .getAll({ appKey, limit: 100 })
      .then(({ data }) => {
        setCredentials(Array.isArray(data) ? data : data.items || []);
      })
      .catch((err) => console.error('Failed to load credentials:', err))
      .finally(() => setLoadingCreds(false));
  }, [script?.appKey]);

  // Computed counts for transport bar
  const completedSteps = useMemo(() => {
    return Object.values(editor.stepStatuses).filter(
      (s) => s === 'passed' || s === 'failed'
    ).length;
  }, [editor.stepStatuses]);

  // Update step statuses from execution progress (Watch Mode)
  useEffect(() => {
    if (executionProgress && executionProgress.step) {
      const idx = executionProgress.step - 1; // 1-based to 0-based
      editor.setStepStatus(idx, executionProgress.status);
      if (executionProgress.status === 'running') {
        setIsRunning(true);
      }
    }
  }, [executionProgress, editor]);

  // Handle execution completion.
  // Guard: only process each executionResult once. Without this, changes to
  // `editor` (e.g. from resetStatuses on Play click) re-trigger this effect
  // with the stale previous result, immediately setting isRunning=false and
  // killing the new run — requiring the user to click Play twice.
  const lastProcessedResult = useRef(null);
  useEffect(() => {
    if (executionResult && executionResult !== lastProcessedResult.current) {
      lastProcessedResult.current = executionResult;
      setIsRunning(false);
      if (executionResult.results) {
        executionResult.results.forEach((r) => {
          editor.setStepStatus(r.step - 1, r.status);
        });
      }
      // Refresh execution history so History tab is up-to-date
      if (script?.id) {
        extensionScriptsService
          .getExecutions(script.id, { limit: 20 })
          .then(({ data }) => {
            setExecutions(Array.isArray(data) ? data : data.items || []);
          })
          .catch(() => {});
      }
    }
  }, [executionResult, editor, script]);

  // Consume pending steps from recording (injected by parent).
  // Insert at the playhead (editor.expandedIndex): recorded steps go AFTER
  // whichever step the user last clicked. If nothing is expanded (null), the
  // steps insert at the beginning. Playhead advances with each batch so
  // successive recordings continue to append at the new end of the group.
  useEffect(() => {
    if (pendingSteps && pendingSteps.length > 0) {
      const afterIndex = editor.expandedIndex;
      if (!isControlled) {
        editor.insertStepsBatch(pendingSteps, afterIndex);
      } else if (onStepsChange) {
        onStepsChange((prev) => {
          const arr = Array.isArray(prev) ? prev : [];
          const base = (afterIndex == null || afterIndex < 0) ? 0 : afterIndex + 1;
          const next = [...arr];
          next.splice(base, 0, ...pendingSteps);
          return next;
        });
      }
      if (onPendingStepsConsumed) {
        onPendingStepsConsumed();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSteps]);

  // Load execution history when switching to History tab
  useEffect(() => {
    if (rightTab === 3 && script?.id && executions.length === 0) {
      setExecutionsLoading(true);
      extensionScriptsService
        .getExecutions(script.id, { limit: 20 })
        .then(({ data }) => {
          setExecutions(Array.isArray(data) ? data : data.items || []);
        })
        .catch((err) => console.error('Failed to load executions:', err))
        .finally(() => setExecutionsLoading(false));
    }
  }, [rightTab, script, executions.length]);

  // Step CRUD handlers (delegate to controlled or hook)
  const handleUpdateStep = useCallback(
    (index, updated) => {
      if (isControlled) {
        const next = [...controlledSteps];
        next[index] = updated;
        onStepsChange(next);
      } else {
        editor.updateStep(index, updated);
      }
    },
    [isControlled, controlledSteps, onStepsChange, editor]
  );

  const handleDeleteStep = useCallback(
    (index) => {
      if (isControlled) {
        onStepsChange(controlledSteps.filter((_, i) => i !== index));
      } else {
        editor.deleteStep(index);
      }
    },
    [isControlled, controlledSteps, onStepsChange, editor]
  );

  const handleDuplicateStep = useCallback(
    (index) => {
      if (isControlled) {
        const clone = JSON.parse(JSON.stringify(controlledSteps[index]));
        clone.name = (clone.name || '') + ' (copy)';
        const next = [...controlledSteps];
        next.splice(index + 1, 0, clone);
        onStepsChange(next);
      } else {
        editor.duplicateStep(index);
      }
    },
    [isControlled, controlledSteps, onStepsChange, editor]
  );

  const handleMoveStep = useCallback(
    (index, direction) => {
      if (isControlled) {
        const target = index + direction;
        if (target < 0 || target >= controlledSteps.length) return;
        const next = [...controlledSteps];
        [next[index], next[target]] = [next[target], next[index]];
        onStepsChange(next);
      } else {
        editor.moveStep(index, direction);
      }
    },
    [isControlled, controlledSteps, onStepsChange, editor]
  );

  const handleReorderStep = useCallback(
    (from, to) => {
      if (isControlled) {
        const next = [...controlledSteps];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        onStepsChange(next);
      } else {
        editor.reorderStep(from, to);
      }
    },
    [isControlled, controlledSteps, onStepsChange, editor]
  );

  const handleAddStep = useCallback(
    (templateKey) => {
      if (isControlled) {
        const step = createStepFromTemplate(templateKey, controlledSteps.length + 1);
        if (step) onStepsChange([...controlledSteps, step]);
      } else {
        editor.addStep(templateKey);
      }
    },
    [isControlled, controlledSteps, onStepsChange, editor]
  );

  const handleToggleExpand = useCallback(
    (idx) => {
      editor.setExpandedIndex(editor.expandedIndex === idx ? null : idx);
    },
    [editor]
  );

  // Transport handlers
  const handlePlay = useCallback(() => {
    setIsRunning(true);
    setIsPaused(false);
    editor.resetStatuses();
    onRunAll && onRunAll(steps, variables, selectedCredentialId);
  }, [onRunAll, steps, variables, selectedCredentialId, editor]);

  const handleStep = useCallback(() => {
    onStepForward && onStepForward(steps, variables, selectedCredentialId);
  }, [onStepForward, steps, variables, selectedCredentialId]);

  const handlePause = useCallback(() => {
    setIsPaused(true);
    onPause && onPause();
  }, [onPause]);

  const handleStop = useCallback(() => {
    setIsRunning(false);
    setIsPaused(false);
    onStop && onStop();
  }, [onStop]);

  const handleRecord = useCallback(() => {
    setIsRecording((prev) => !prev);
    onRecord && onRecord(!isRecording);
  }, [onRecord, isRecording]);

  // LaunchBar handlers (web editor only)
  const handleLaunchExtension = useCallback(() => {
    onRunInExtension && onRunInExtension(steps, variables, selectedCredentialId);
  }, [onRunInExtension, steps, variables, selectedCredentialId]);

  // `engine` arrives from LaunchBar's Watch Mode split-button:
  // 'extension-v4' | 'playwright'. Forward as a 4th positional arg so the
  // page-level callback can route to v4 watch (production-mirror live) vs
  // pure Playwright watch (clean baseline). Defaults to 'extension-v4'.
  const handleLaunchWatchMode = useCallback((engine = 'extension-v4') => {
    onWatchMode && onWatchMode(steps, variables, selectedCredentialId, engine);
  }, [onWatchMode, steps, variables, selectedCredentialId]);

  // `mode` arrives from LaunchBar's Run Headless split-button:
  // 'playwright' | 'extension-v4'. Forward it as a 4th positional arg so the
  // page-level callback can route to /headless/run vs /headless/run-v5
  // (the backend branches on extensionVariant). Defaults to 'playwright'.
  const handleLaunchHeadless = useCallback((mode = 'playwright') => {
    onRunHeadless && onRunHeadless(steps, variables, selectedCredentialId, mode);
  }, [onRunHeadless, steps, variables, selectedCredentialId]);

  // Credential change with sync callback
  const handleCredentialChange = useCallback((_, cred) => {
    const id = cred?.id || '';
    setSelectedCredentialId(id);
    onCredentialChange && onCredentialChange(id, script?.appKey);
  }, [onCredentialChange, script?.appKey]);

  // Show TransportBar (Play/Step/Pause/Stop/Record) when we have execution callbacks
  // (extension sidebar or Watch Mode), LaunchBar otherwise (web editor).
  const useTransportBar = externalExecMode === 'extension' || externalExecMode === 'watch' || !!onRunAll;

  const handleSettingsChange = useCallback(
    (changes) => {
      if (!script) return;
      // Update script metadata (will be saved with next save)
      Object.assign(script, changes);
    },
    [script]
  );

  // Loading state
  if (editor.loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={24} sx={{ mr: 1 }} />
        <Typography variant="body2" color="text.secondary">Loading script...</Typography>
      </Box>
    );
  }

  // Error state
  if (editor.error && !steps.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{editor.error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Tab bar */}
      <Tabs
        value={rightTab}
        onChange={(_, v) => setRightTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          borderBottom: '1px solid #e0e0e0',
          minHeight: 42,
          '& .MuiTab-root': { minHeight: 42, textTransform: 'none', fontSize: '0.85rem' },
        }}
      >
        <Tab label="Steps" />
        <Tab label="Variables" />
        <Tab label="Settings" />
        <Tab label="History" />
        <Tab label="Output" />
      </Tabs>

      {/* Scrollable content area */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          pb: rightTab === 0 && editor.isDirty ? '64px' : 0,
          position: 'relative',
        }}
      >
        {/* STEPS TAB */}
        <TabPanel value={rightTab} index={0}>
          {/* TransportBar for extension/watch mode, LaunchBar for web editor */}
          {useTransportBar ? (
            <TransportBar
              totalSteps={steps.length}
              completedSteps={completedSteps}
              isRunning={isRunning}
              isPaused={isPaused}
              isRecording={isRecording}
              onPlay={handlePlay}
              onStep={handleStep}
              onPause={handlePause}
              onStop={handleStop}
              onRecord={handleRecord}
            />
          ) : (
            <LaunchBar
              onRunInExtension={handleLaunchExtension}
              onWatchMode={handleLaunchWatchMode}
              onRunHeadless={handleLaunchHeadless}
            />
          )}

          {/* Credential dropdown (searchable) */}
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
            <Autocomplete
              size="small"
              options={credentials}
              loading={loadingCreds}
              filterOptions={credFilterOptions}
              getOptionLabel={formatCredLabel}
              value={credentials.find((c) => c.id === selectedCredentialId) || null}
              onChange={handleCredentialChange}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Credential"
                  placeholder="Type to search..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingCreds ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>

          <StepList
            steps={steps}
            expandedIndex={editor.expandedIndex}
            stepStatuses={editor.stepStatuses}
            onToggleExpand={handleToggleExpand}
            onUpdateStep={handleUpdateStep}
            onDeleteStep={handleDeleteStep}
            onDuplicateStep={handleDuplicateStep}
            onMoveStep={handleMoveStep}
            onReorderStep={handleReorderStep}
            onRunStep={onRunStep}
            onAddStep={handleAddStep}
            onPickSelector={onPickSelector}
          />
        </TabPanel>

        {/* VARIABLES TAB */}
        <TabPanel value={rightTab} index={1}>
          <VariablesPanel
            steps={steps}
            variables={variables}
            onVariablesChange={setVariables}
            selectedCredentialId={selectedCredentialId}
            onCredentialChange={setSelectedCredentialId}
            appKey={script?.appKey}
          />
        </TabPanel>

        {/* SETTINGS TAB */}
        <TabPanel value={rightTab} index={2}>
          <SettingsPanel
            script={script}
            onSettingsChange={handleSettingsChange}
          />
        </TabPanel>

        {/* HISTORY TAB */}
        <TabPanel value={rightTab} index={3}>
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Execution History
            </Typography>

            {/* Recording mode banner — reads from script.recordMode. "Change…"
                opens a modal that PUTs an update to /api/extension-scripts/:id. */}
            {(() => {
              const currentMode = script?.recordMode || 'failures_only';
              const modeLabels = {
                off: { label: 'Off', color: '#64748b', blurb: 'No videos or screenshots captured. Step-level data only.' },
                failures_only: { label: 'Failures only', color: '#78350f', blurb: 'Videos + screenshots captured on step failure. 30-day retention.' },
                full: { label: 'Full (debug)', color: '#6b21a8', blurb: 'Every run fully recorded (video + AI analysis). Admin / debug use.' },
              };
              const m = modeLabels[currentMode] || modeLabels.failures_only;
              const bg = currentMode === 'off' ? '#f1f5f9' : currentMode === 'full' ? '#faf5ff' : '#fef3c7';
              const border = currentMode === 'off' ? '#cbd5e1' : currentMode === 'full' ? '#e9d5ff' : '#fde68a';
              const dot = currentMode === 'off' ? '#94a3b8' : currentMode === 'full' ? '#7c3aed' : '#dc2626';
              return (
                <Box sx={{
                  mb: 2, p: 1.25, px: 1.5, background: bg, border: `1px solid ${border}`,
                  borderRadius: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
                }}>
                  <Box sx={{
                    width: 8, height: 8, borderRadius: '50%', background: dot,
                    animation: currentMode !== 'off' ? 'amxPulse 1.4s infinite' : 'none',
                    '@keyframes amxPulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
                  }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, color: m.color }}>Recording mode:</Typography>
                  <Chip size="small" label={m.label} sx={{ height: 22, background: 'white', fontWeight: 600 }} />
                  <Typography variant="caption" sx={{ color: m.color, fontSize: 12 }}>{m.blurb}</Typography>
                  <Box sx={{ ml: 'auto' }}>
                    <Typography
                      variant="caption"
                      sx={{ color: '#1976d2', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                      onClick={() => {
                        setRecordModeDraft(currentMode);
                        setRecordModeModal({ open: true, saving: false, error: null });
                      }}
                    >
                      Change…
                    </Typography>
                  </Box>
                </Box>
              );
            })()}

            {/* Change-mode modal */}
            <Dialog open={recordModeModal.open} onClose={() => !recordModeModal.saving && setRecordModeModal({ open: false, saving: false, error: null })} maxWidth="sm" fullWidth>
              <DialogTitle>Change recording mode</DialogTitle>
              <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Controls what gets captured on every run of <b>{script?.name}</b>. Change takes effect on the next run.
                </Typography>
                <FormControl>
                  <RadioGroup
                    value={recordModeDraft || 'failures_only'}
                    onChange={(e) => setRecordModeDraft(e.target.value)}
                  >
                    {[
                      { v: 'off',           label: 'Off',             blurb: 'Step-level data only. No videos or screenshots. Lowest disk use, no diagnostic artifacts on failures.' },
                      { v: 'failures_only', label: 'Failures only (recommended)', blurb: 'Capture video + screenshot only when a step fails. Discarded on success. 30-day retention.' },
                      { v: 'full',          label: 'Full (debug)',    blurb: 'Capture every run. Triggers AI analysis on every run. Admin / debugging use — raises storage and API cost.' },
                    ].map((opt) => (
                      <FormControlLabel
                        key={opt.v}
                        value={opt.v}
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{opt.label}</Typography>
                            <Typography variant="caption" color="text.secondary">{opt.blurb}</Typography>
                          </Box>
                        }
                        sx={{ alignItems: 'flex-start', mb: 1, '& .MuiFormControlLabel-label': { pt: 0.5 } }}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
                {recordModeModal.error && <Alert severity="error" sx={{ mt: 1 }}>{recordModeModal.error}</Alert>}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setRecordModeModal({ open: false, saving: false, error: null })} disabled={recordModeModal.saving}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  disabled={recordModeModal.saving || !recordModeDraft || recordModeDraft === script?.recordMode}
                  onClick={async () => {
                    setRecordModeModal((m) => ({ ...m, saving: true, error: null }));
                    try {
                      await extensionScriptsService.update(script.id, { recordMode: recordModeDraft });
                      // Tell the rest of the editor to refresh
                      handleSettingsChange({ recordMode: recordModeDraft });
                      setRecordModeModal({ open: false, saving: false, error: null });
                    } catch (err) {
                      const msg = err?.response?.data?.message || err?.message || 'Failed to update recording mode';
                      setRecordModeModal((m) => ({ ...m, saving: false, error: msg }));
                    }
                  }}
                >
                  {recordModeModal.saving ? 'Saving…' : 'Save'}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Stats strip */}
            {executions.length > 0 && (() => {
              const now = Date.now();
              const D7 = now - 7 * 24 * 3600 * 1000;
              const D30 = now - 30 * 24 * 3600 * 1000;
              const runs7d = executions.filter((e) => new Date(e.startedAt || e.date).getTime() >= D7);
              const passed7d = runs7d.filter((e) => e.status === 'passed' || e.status === 'completed').length;
              const passRate7d = runs7d.length > 0 ? Math.round((passed7d / runs7d.length) * 100) : null;
              const avgDurMs = executions.length > 0
                ? executions.reduce((a, e) => a + (e.durationMs || 0), 0) / executions.length
                : 0;
              const failures30d = executions.filter((e) => new Date(e.startedAt || e.date).getTime() >= D30 && e.status !== 'passed' && e.status !== 'completed').length;
              const last = executions[0];
              const lastAgo = last && last.startedAt ? Math.round((now - new Date(last.startedAt).getTime()) / 60000) : null; // minutes
              const lastLabel = lastAgo == null ? '—'
                : lastAgo < 1 ? 'just now'
                : lastAgo < 60 ? `${lastAgo} min ago`
                : lastAgo < 60 * 24 ? `${Math.round(lastAgo / 60)} hr ago`
                : `${Math.round(lastAgo / 60 / 24)} d ago`;
              const stats = [
                { label: 'Pass rate (7d)', value: passRate7d == null ? '—' : `${passRate7d}%`, sub: `${passed7d} of ${runs7d.length} runs`,
                  color: passRate7d != null && passRate7d >= 90 ? '#16a34a' : passRate7d != null && passRate7d > 0 ? '#d97706' : '#dc2626' },
                { label: 'Avg duration', value: avgDurMs > 0 ? `${(avgDurMs / 1000).toFixed(1)}s` : '—', sub: `${executions.length} runs observed` },
                { label: 'Failures (30d)', value: String(failures30d), sub: failures30d > 0 ? 'see red rows below' : 'none',
                  color: failures30d > 0 ? '#dc2626' : '#16a34a' },
                { label: 'Last run', value: lastLabel, sub: last ? `${last.trigger || '—'} · ${last.status}` : '—' },
              ];
              return (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5, mb: 2 }}>
                  {stats.map((s, i) => (
                    <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', fontWeight: 600, fontSize: 10 }}>
                        {s.label}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.25, color: s.color || 'text.primary', fontSize: s.value.length > 10 ? 14 : 20, lineHeight: 1.2 }}>
                        {s.value}
                      </Typography>
                      {s.sub && <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>{s.sub}</Typography>}
                    </Paper>
                  ))}
                </Box>
              );
            })()}

            {executionsLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">Loading history...</Typography>
              </Box>
            ) : executions.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No executions recorded yet.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: 28 }}></TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>When</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Trigger</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Engine</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Credential</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Steps</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {executions.map((exec) => {
                    const isExpanded = !!expandedExecutions[exec.id];
                    const passed = exec.status === 'passed' || exec.status === 'completed';
                    const durationText = exec.durationMs != null
                      ? `${(exec.durationMs / 1000).toFixed(1)}s`
                      : (exec.duration || '-');
                    const stepResults = Array.isArray(exec.stepResults) ? exec.stepResults : [];
                    const finalUrl = exec.metadata?.finalUrl;
                    const credLabel = exec.credential || exec.credentialVaultId || exec.credentialId || '-';
                    const credShort = credLabel && credLabel.length > 12 ? credLabel.substring(0, 8) + '…' : credLabel;
                    const toggle = () => setExpandedExecutions((prev) => ({ ...prev, [exec.id]: !prev[exec.id] }));
                    const rowBg = passed
                      ? (isExpanded ? '#fafbfc' : undefined)
                      : '#fff7f7';
                    return (
                      <React.Fragment key={exec.id}>
                        <TableRow
                          hover
                          sx={{ cursor: 'pointer', backgroundColor: rowBg, '& > td': { borderBottom: isExpanded ? 'none' : undefined } }}
                          onClick={toggle}
                        >
                          <TableCell sx={{ pr: 0 }}>
                            <IconButton size="small" sx={{ transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>
                              <ChevronIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {exec.startedAt ? new Date(exec.startedAt).toLocaleString() : exec.date || ''}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {passed ? (
                                <PassIcon sx={{ color: '#16a34a', fontSize: 16 }} />
                              ) : (
                                <FailIcon sx={{ color: '#dc2626', fontSize: 16 }} />
                              )}
                              <Typography variant="caption" sx={{ textTransform: 'capitalize', fontWeight: 600, color: passed ? '#16a34a' : '#dc2626' }}>
                                {exec.status}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{durationText}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={exec.trigger || '-'} sx={{ height: 20, fontSize: 10, textTransform: 'capitalize' }} />
                          </TableCell>
                          <TableCell>
                            <ExecutorModeChip exec={exec} />
                          </TableCell>
                          <TableCell>
                            <Tooltip title={credLabel} arrow>
                              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                {credShort}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                              {exec.stepsCompleted != null
                                ? `${exec.stepsCompleted}/${exec.stepsTotal || steps.length}`
                                : exec.steps || '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow sx={{ backgroundColor: rowBg }}>
                            <TableCell colSpan={8} sx={{ py: 0, pb: 2, borderBottom: '1px solid #e2e8f0' }}>
                              <Box sx={{ pl: 5, pr: 2, pt: 1 }}>
                                {/* Step results */}
                                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                  Step results {stepResults.length > 0 ? `(${stepResults.filter((r) => r.status === 'passed').length}/${stepResults.length})` : ''}
                                </Typography>
                                {stepResults.length === 0 ? (
                                  <Typography variant="caption" color="text.secondary">No per-step data on this run.</Typography>
                                ) : (
                                  <Paper variant="outlined" sx={{ mb: 1.5, overflow: 'hidden' }}>
                                    {stepResults.map((r, i) => {
                                      const stepPassed = r.status === 'passed';
                                      const stepFailed = r.status === 'failed';
                                      const stepSkipped = r.status === 'skipped';
                                      return (
                                        <Box
                                          key={i}
                                          sx={{
                                            display: 'grid',
                                            gridTemplateColumns: '20px 32px 1fr 80px 1.2fr',
                                            gap: 1,
                                            px: 1.5,
                                            py: 0.7,
                                            alignItems: 'center',
                                            borderBottom: i < stepResults.length - 1 ? '1px solid #eef2f6' : 'none',
                                            backgroundColor: stepFailed ? '#fef2f2' : stepSkipped ? '#f8fafc' : 'white',
                                          }}
                                        >
                                          {stepPassed ? <PassIcon sx={{ color: '#16a34a', fontSize: 14 }} />
                                           : stepFailed ? <FailIcon sx={{ color: '#dc2626', fontSize: 14 }} />
                                           : <SkippedIcon sx={{ color: '#94a3b8', fontSize: 14 }} />}
                                          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{r.step}</Typography>
                                          <Typography variant="caption" sx={{ color: stepSkipped ? 'text.secondary' : 'inherit' }}>
                                            {r.stepName || r.name || `Step ${r.step}`}
                                          </Typography>
                                          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', textAlign: 'right' }}>
                                            {r.durationMs != null
                                              ? r.durationMs > 1000 ? `${(r.durationMs / 1000).toFixed(1)}s` : `${r.durationMs}ms`
                                              : stepSkipped ? 'skipped' : ''}
                                          </Typography>
                                          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#dc2626', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {r.error || ''}
                                          </Typography>
                                        </Box>
                                      );
                                    })}
                                  </Paper>
                                )}

                                {/* Final URL */}
                                {finalUrl && (
                                  <>
                                    <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3, color: 'text.secondary', display: 'block', mb: 0.5, mt: 1 }}>
                                      Final URL
                                    </Typography>
                                    <Box sx={{ p: 1, backgroundColor: '#f8fafc', borderRadius: 1, border: '1px solid #e2e8f0', fontFamily: 'monospace', fontSize: 11, display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                      <Box sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{finalUrl}</Box>
                                      <a href={finalUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex' }}>
                                        <ExternalIcon sx={{ fontSize: 14, color: '#1976d2' }} />
                                      </a>
                                    </Box>
                                  </>
                                )}

                                {/* Error summary (overall) */}
                                {exec.error && (
                                  <Alert severity="error" sx={{ mt: 1, '& .MuiAlert-message': { fontSize: 12, fontFamily: 'monospace' } }}>
                                    {exec.error}
                                  </Alert>
                                )}

                                {/* Screenshot (R-1 scope) */}
                                {exec.screenshotUrl && (
                                  <>
                                    <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3, color: 'text.secondary', display: 'block', mb: 0.5, mt: 1 }}>
                                      Failure screenshot
                                    </Typography>
                                    <a href={exec.screenshotUrl} target="_blank" rel="noopener noreferrer">
                                      <img src={exec.screenshotUrl} alt="failure screenshot" style={{ maxWidth: 320, border: '1px solid #e2e8f0', borderRadius: 4 }} />
                                    </a>
                                  </>
                                )}

                                {/* Video placeholder (R-2) */}
                                {exec.metadata?.videoUrl && (
                                  <>
                                    <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3, color: 'text.secondary', display: 'block', mb: 0.5, mt: 1 }}>
                                      Run video
                                    </Typography>
                                    <video src={exec.metadata.videoUrl} controls style={{ maxWidth: 520, borderRadius: 4, border: '1px solid #e2e8f0' }} />
                                  </>
                                )}

                                {/* AI analysis (R-3). Displays populated analysis if present,
                                    else a placeholder explaining the PII-free policy. */}
                                <Box sx={{
                                  mt: 1.5, border: '1px solid #e9d5ff',
                                  background: 'linear-gradient(135deg, #faf5ff 0%, #f5f3ff 100%)',
                                  borderRadius: 1.25, p: 1.5,
                                }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                                    <Box sx={{
                                      width: 20, height: 20, borderRadius: 0.5,
                                      background: 'linear-gradient(135deg, #a855f7, #6366f1)',
                                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                      color: 'white', fontSize: 11, fontWeight: 700,
                                    }}>✦</Box>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#6b21a8', textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 11 }}>
                                      AI analysis
                                    </Typography>
                                    {exec.metadata?.aiAnalysis && (
                                      <Typography variant="caption" sx={{ ml: 'auto', color: '#a78bfa', fontSize: 10 }}>
                                        {exec.metadata.aiAnalysis.model || 'claude-opus-4-6'}
                                        {exec.metadata.aiAnalysis.durationMs && ` · ${(exec.metadata.aiAnalysis.durationMs / 1000).toFixed(0)}s`}
                                      </Typography>
                                    )}
                                  </Box>
                                  {exec.metadata?.aiAnalysis ? (
                                    <>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: passed ? '#065f46' : '#991b1b', mb: 0.5, fontSize: 12.5 }}>
                                        {passed ? '✓ ' : '✗ '}
                                        {exec.metadata.aiAnalysis.verdict || (passed ? 'Run looks healthy.' : 'Failure analysis:')}
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: '#312e81', fontSize: 12.5, lineHeight: 1.5 }}>
                                        {exec.metadata.aiAnalysis.summary}
                                      </Typography>
                                      {Array.isArray(exec.metadata.aiAnalysis.suggestions) && exec.metadata.aiAnalysis.suggestions.length > 0 && (
                                        <Box sx={{ mt: 1, p: 1, background: 'white', borderRadius: 1, border: '1px solid #e9d5ff' }}>
                                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.3 }}>
                                            Recommended fixes
                                          </Typography>
                                          <Box component="ol" sx={{ m: 0, pl: 2.5, fontSize: 12, color: '#1e293b' }}>
                                            {exec.metadata.aiAnalysis.suggestions.map((s, i) => (
                                              <li key={i} style={{ marginTop: 4 }}>{s}</li>
                                            ))}
                                          </Box>
                                        </Box>
                                      )}
                                    </>
                                  ) : (
                                    <Typography variant="caption" sx={{ color: '#a78bfa', fontStyle: 'italic', fontSize: 12 }}>
                                      {passed
                                        ? 'Analysis skipped — run passed in Failures-only mode (nothing to diagnose).'
                                        : 'Analysis pending — AI diagnosis runs automatically after failure video is captured.'}
                                    </Typography>
                                  )}
                                  <Box sx={{ mt: 1, pt: 0.75, borderTop: '1px dashed #e9d5ff', display: 'flex', alignItems: 'center', gap: 1, fontSize: 11, color: '#a78bfa' }}>
                                    <Chip size="small" label="PII-free" sx={{ height: 16, fontSize: 9.5, background: 'white', color: '#4338ca', fontWeight: 700, border: '1px solid #c7d2fe' }} />
                                    <Typography variant="caption" sx={{ fontSize: 10.5 }}>
                                      Scope: automation mechanics only (selectors, timing, step flow). User content NOT transcribed.
                                    </Typography>
                                  </Box>
                                </Box>

                                {/* Open full run detail */}
                                <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                                  <Typography
                                    variant="caption"
                                    onClick={() => navigate(`/scripts/${script.id}/runs/${exec.id}`)}
                                    sx={{
                                      color: 'primary.main', fontWeight: 600, cursor: 'pointer',
                                      fontSize: 12, '&:hover': { textDecoration: 'underline' },
                                    }}
                                  >
                                    Open full run detail →
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Box>
        </TabPanel>

        {/* OUTPUT TAB */}
        <TabPanel value={rightTab} index={4}>
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Execution Output
            </Typography>
            <Paper
              variant="outlined"
              sx={{ p: 2, bgcolor: '#1a1a2e', borderRadius: 1, overflow: 'auto', maxHeight: 500 }}
            >
              <pre
                style={{
                  color: '#a5d6a7',
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {script
                  ? JSON.stringify(
                      {
                        scriptId: script.id,
                        scriptName: script.name,
                        status: isRunning ? 'running' : 'idle',
                        stepsTotal: steps.length,
                        stepsCompleted: completedSteps,
                      },
                      null,
                      2
                    )
                  : '// No script loaded'}
              </pre>
            </Paper>
          </Box>
        </TabPanel>
      </Box>

      {/* Save bar (sticky bottom, Steps tab only) */}
      {rightTab === 0 && !isControlled && (
        <SaveBar
          isDirty={editor.isDirty}
          canUndo={editor.canUndo}
          saving={editor.saving}
          onUndo={editor.undo}
          onDiscard={editor.discard}
          onSave={editor.saveScript}
        />
      )}
    </Box>
  );
};

export default UnifiedStepEditor;
